import { DataGridPro, GridApiPro } from "@mui/x-data-grid-pro"
import { Box, Stack, Typography } from "@mui/material"
import { useAppDispatch, useAppSelector } from "@app/hooks"
import appTheme from "../../theme/appTheme"
import { getLegacyProgramsMenuColumns } from "./programsMenuColumns"
import { ExpandedRowContent } from "./programsMenuCells"
import { selectedProgramsUpdated } from "@features/program/programsMenuSlice"
import { ProgramDisplayData } from "./programsMenu"
import { selectSearchedPrograms } from "@features/search/searchSlice"
import { fetchPrograms } from "@features/program/programSliceThunks"
import { LoadingComponent } from "@cmp/loaders/loadingComponent"
import { QueryErrorPanel } from "@cmp/loaders/queryStatePanel"
import { RootState } from "@app/store"

export function LegacyProgramsTable({
  data,
  gridApiRef,
}: {
  data: ProgramDisplayData[]
  gridApiRef: React.MutableRefObject<GridApiPro>
}) {
  const dispatch = useAppDispatch()

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: "100%",
        bgcolor: appTheme.palette.eTypes.sand15,
      }}
    >
      <DataGridPro
        apiRef={gridApiRef}
        rows={data}
        columns={getLegacyProgramsMenuColumns()}
        disableColumnMenu
        checkboxSelection
        disableRowSelectionOnClick
        disableColumnReorder
        hideFooter
        slots={{ noRowsOverlay: LegacyProgramsNoRowsOverlay }}
        getRowId={(row) => row.id}
        onRowSelectionModelChange={(newSelection) => {
          const stringIds = newSelection.filter(
            (id) => typeof id === "string",
          ) as string[]
          dispatch(selectedProgramsUpdated(stringIds))
        }}
        getDetailPanelContent={(params) => (
          <ExpandedRowContent program={params.row} />
        )}
        getDetailPanelHeight={() => "auto"}
        getRowHeight={() => "auto"}
        initialState={{
          sorting: {
            sortModel: [{ field: "startDate", sort: "desc" }],
          },
        }}
        sx={{
          border: "none",
          "& .MuiDataGrid-row": {
            backgroundColor: "white",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: appTheme.palette.action.hover,
          },
          "& .MuiDataGrid-cell": {
            p: "0px !important",
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-checkboxInput": {
            p: "0px !important",
          },
          "& .MuiSvgIcon-root": {
            width: "16px !important",
            height: "16px !important",
          },
          "& .expand-cell": {
            width: "32px !important",
          },
          "& .domain-header": {
            width: "216px !important",
          },
          "& .expand-header": {
            width: "6px !important",
          },
          "& .MuiDataGrid-columnHeader:focus": {
            outline: "none !important",
          },
          "& .MuiDataGrid-columnHeader": {
            padding: "0px",
          },
          "& .MuiDataGrid-columnHeader:focus-within": {
            outline: "none !important",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
        }}
      />
    </Box>
  )
}

// Honest zero state: tell apart "still loading", "the fetch failed", and
// "nothing matches this search/filter" instead of a silent blank grid.
function LegacyProgramsNoRowsOverlay() {
  const dispatch = useAppDispatch()
  const loadingState = useAppSelector(
    (state: RootState) => state.program.loadingState,
  )
  const { text, advanceFilters } = useAppSelector(selectSearchedPrograms)
  const hasActiveFilters = advanceFilters.some(
    (filter) => filter.selectedValues.length > 0,
  )

  if (loadingState === "idle" || loadingState === "loading") {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <LoadingComponent loadedComponents="programs" />
      </Stack>
    )
  }

  if (loadingState === "failed") {
    return (
      <QueryErrorPanel
        message="Something went wrong while loading programs."
        onRetry={() => dispatch(fetchPrograms())}
      />
    )
  }

  const message = text
    ? `No programs match "${text}"${
        hasActiveFilters ? " with the active filters" : ""
      }`
    : hasActiveFilters
    ? "No programs match the active filters"
    : "No programs yet"

  return (
    <Stack height="100%" alignItems="center" justifyContent="center" gap={0.5}>
      <Typography variant="body1_sb">{message}</Typography>
      {(text || hasActiveFilters) && (
        <Typography variant="body2" color="text.secondary">
          Try a different search or remove a filter above.
        </Typography>
      )}
    </Stack>
  )
}
